import Vue from 'vue'
import api from '@/libs/api'
import { ROUTE_AUTH, ROUTE_ATTENDANCE_NEW } from '@/router/constants'
import { hasProperty } from '@/libs/helpers'
import { settingValue, ungatedDefaults } from '@/libs/settingsFields'
import DTMFAudio from '@/libs/dtmfAudio'

const router = document.router
const goToAuth = () => router.push({ name: ROUTE_AUTH }).catch(() => { })
const isLoggedAgent = (rootState) => {
  const status = rootState?.agentModule?.session?.agent?.status
  return Boolean(status && status !== 'OFFLINE')
}

const actions = {
  async saveSettings ({ commit, state, rootState, dispatch }, config) {
    try {
      commit('SET_LOADING')
      config.customer = getCustomer()

      const isLogged = isLoggedAgent(rootState)
      const isConnected = rootState.connectionModule.connected
      const keepCustomer = state.customer === config.customer
      const keepProvision = isLogged && isConnected && keepCustomer

      if (keepProvision) {
        commit('SET_CONFIG', config)
        commit('UPDATE_DTMF_OUTPUT_DEVICES')
        dispatch('configOutput', 'remoteAudio')
        return
      }

      const provision = await api.setConfiguration(config.customer, currentAuthorization())

      commit('SET_SETTINGS', { config, provision })
      commit('UPDATE_DTMF_OUTPUT_DEVICES')
      dispatch('configOutput', 'remoteAudio')
    } catch (err) {
      commit('SET_SETTINGS_ERROR', err.message)
    }
  },

  updateDTMFOutputDevices ({ commit }) {
    commit('UPDATE_DTMF_OUTPUT_DEVICES')
  },

  configOutput ({ getters }, elementId) {
    const audioElement = document.getElementById(elementId)
    if (audioElement) audioElement.setSinkId(getters.output)
  },

  setLoaded ({ getters }, elementId) {
    const audioElement = document.getElementById(elementId)
    if (audioElement) audioElement.setSinkId(getters.output)
  },

  clearError: ({ commit }) => commit('SET_SETTINGS_ERROR', null)
}

const mutations = {
  SET_LOADING (state) {
    state.loading = true
  },

  SET_CONFIG (state, config) {
    mergeConfig(state, config)
  },

  SET_SETTINGS (state, { config }) {
    mergeConfig(state, config)
  },

  SET_SETTINGS_ERROR (state, error) {
    Object.assign(state, { error, loading: false })
  },

  UPDATE_DTMF_OUTPUT_DEVICES (state) {
    DTMFAudio.setOutputDevices(settingValue(state, 'additionalDevice'))
  }
}

const mergeConfig = (state, config) => {
  for (const [key, value] of Object.entries(config ?? {})) {
    if (value === undefined) continue
    if (hasProperty(state, key)) state[key] = value
    else Vue.set(state, key, value)
  }

  Object.assign(state, { error: null, loading: false })
}

const middlewares = {
  'settingsModule/SET_SETTINGS': async ({ dispatch, state }, { payload }) => {
    const disableDND = payload?.config?.useDND !== 'yes' && state.agentModule.autoRejection
    if (disableDND) dispatch('agentModule/setAutoRejection', false, { root: true })

    if (!isLoggedAgent(state)) goToAuth()
  },

  'settingsModule/SET_CONFIG': async ({ dispatch, state }, { payload }) => {
    const disableDND = payload?.useDND !== 'yes' && state.agentModule.autoRejection
    if (disableDND) dispatch('agentModule/setAutoRejection', false, { root: true })

    const isOffline = ['', 'OFFLINE'].includes(String(state?.agentModule?.session?.agent?.status || '').trim())

    if (isOffline) {
      dispatch('socketModule/finish', null, { root: true })
      return router.push({ name: ROUTE_AUTH })
    }

    router.push({ name: ROUTE_ATTENDANCE_NEW })
  }
}

const currentAuthorization = () => {
  const authorization = api.getAuthorization()
  return authorization?.token ? authorization : null
}

const getCustomer = () => {
  const host = window.location.hostname
  return host.includes('.vp.vonixcc.com.br') ? host.split('.').at(0) : 'blackbox'
}

const getters = {
  customer: (state) => state.customer,
  input: (state) => settingValue(state, 'inputPhone'),
  output: (state) => settingValue(state, 'outputPhone'),
  windowToForeground: (state) => settingValue(state, 'windowToForeground'),
  areaCode: ({ provision }) => provision?.areaCode,
  provision: ({ provision }) => provision,
  token: ({ provision }) => provision?.authorization?.token,
  additionalDevice: (state) => settingValue(state, 'additionalDevice'),
  notificationStyle: (state) => settingValue(state, 'notificationStyle'),
  groupMinPerfilator: (state) => settingValue(state, 'groupMinPerfilator'),

  canUseDND: (state, getters, rootState, rootGetters) => rootGetters['agentModule/flag']('canUseDND') === true,
  useDND: (state, { canUseDND }) => canUseDND && settingValue(state, 'useDND') === 'yes',
  dndOptionsList: (state, { useDND }) => (useDND ? settingValue(state, 'dndOptionsList') : []),

  hasChatRejection: (state, { dndOptionsList }) => dndOptionsList.includes('TEXT'),
  hasMailRejection: (state, { dndOptionsList }) => dndOptionsList.includes('MAIL'),
  hasCallsRejection: (state, { dndOptionsList }) => {
    if (!dndOptionsList.includes('VOICE')) return false
    return settingValue(state, 'dndCallTypes')
  },
  autoAlertVolume: (state) => settingValue(state, 'autoAlertVolume'),
  isAdmin: () => false
}

export default {
  namespaced: true,

  getters,
  actions,
  mutations,
  middlewares,

  state: () => ({
    error: null,
    loading: false,

    customer: '',

    ...ungatedDefaults()
  })
}
